/**
 * # Row facades — ONE facade per (Sheet, RowLocalId)
 *
 * The DOM binder and the app key state by a row facade's identity: a
 * renderer's `readIdentity` tuple compares the row by `===`, a WeakMap keyed
 * by the row holds its UI state. So a sheet hands out ONE facade object per
 * row for its lifetime (`Sheet.facadeFor`), whichever door reached the row —
 * the sheet's row list, its joined tree (rebuilt on every structural change),
 * a formula's walk — and across re-keys and the COW promote (the row keeps
 * its `RowLocalId`).
 *
 * The facade itself is the SDK's (`SheetRow.Handle`): the sheet only interns
 * it. Its owner — the SDK's handle over the sheet — mints it the first time
 * (`RowFacade.Owner.mint`) and answers its calls; a sheet shared by two
 * handles answers through the newest one still holding it (`Table.own`).
 */
import type { Option } from "../core/option.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";

/** A row's facade as its sheet interns it: the object every door onto the row hands out. */
export interface RowFacade {
  readonly id: RowLocalId;
}

/** Types and the intern table that go with `RowFacade`. */
export namespace RowFacade {
  /** Who mints and answers a sheet's row facades: the SDK's handle over the sheet. */
  export interface Owner {
    /** Build the facade of row `id` — called once per row: the sheet interns the answer. */
    mint(id: RowLocalId): RowFacade;
  }

  /**
   * A sheet's row facades (`Sheet.facadeFor`): one per `RowLocalId`, minted
   * by the newest owner still holding the sheet and kept for the sheet's
   * lifetime.
   */
  export class Table {
    readonly #byId = new Map<RowLocalId, RowFacade>();
    readonly #owners: Owner[] = [];

    /**
     * Register `owner` (the newest answers from now on); the answer
     * unregisters it — the one before it answers again, the facades stay.
     */
    own(owner: Owner): () => void {
      this.#owners.push(owner);
      return () => {
        const at = this.#owners.lastIndexOf(owner);
        if (at >= 0) this.#owners.splice(at, 1);
      };
    }

    /** The newest owner still holding the sheet, `null` when none does. */
    get owner(): Option<Owner> {
      return this.#owners[this.#owners.length - 1] ?? null;
    }

    /** Row `id`'s facade: the interned one, else minted by the owner now. Throws when no owner holds the sheet. */
    facadeFor(id: RowLocalId): RowFacade {
      const known = this.#byId.get(id);
      if (known != null) return known;
      const owner = this.owner;
      if (owner == null) throw new Error(`RowFacade.Table.facadeFor: no owner holds the sheet of row ${id.toString()}`);
      const minted = owner.mint(id);
      this.#byId.set(id, minted);
      return minted;
    }

    /** Row `id`'s facade if one was minted, else `null` (none minted). */
    peek(id: RowLocalId): Option<RowFacade> {
      return this.#byId.get(id) ?? null;
    }
  }
}
